import { Buffer } from "buffer";
import { Coords, TestDataBuffer } from "@/types";
import { serverUrl, testDataOnStart } from "@/config/default";

export class DataBufferWriter {
  private static instance: DataBufferWriter;
  private data: TestDataBuffer;
  private timeStart: number = 0;
  private mouseListener;
  private _audioChunks;
  private constructor() {
    this.restartTest();
  }

  /**
   * Заменяет длинные ключи объекта на короткие (например, "name" → "n")
   */
  private minifyJsonKeys(data: any): any {
    if (Array.isArray(data)) {
      return data.map((item) => this.minifyJsonKeys(item));
    } else if (data && typeof data === "object") {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(data)) {
        // Заменяем ключ на сокращенный (можно добавить свою логику)
        const shortKey = this.getShortKey(key);
        result[shortKey] = this.minifyJsonKeys(value);
      }
      return result;
    }
    return data;
  }

  /**
   * Маппинг длинных ключей в короткие (можно расширить)
   */
  private getShortKey(key: string): string {
    const keyMap: Record<string, string> = {
      id: "i",
      name: "n",
      value: "v",
      data: "d",
      items: "is",
      timestamp: "t",
      // ... добавить другие ключи по необходимости
    };
    return keyMap[key] || key; // если ключ не найден, оставляем как есть
  }

  public static getInstance(): DataBufferWriter {
    if (!DataBufferWriter.instance) {
      DataBufferWriter.instance = new DataBufferWriter();
    }
    return DataBufferWriter.instance;
  }

  public startMouseTracker() {
    this.mouseListener = window.addEventListener("mousemove", (event) => {
      const coords: Coords = {
        x: event.clientX,
        y: event.clientY,
      };
      const timestamp = new Date().getTime() - this.timeStart;
      this.data.global.push({
        point: coords,
        timestamp,
      });
    });
  }

  public stopMouseTracker() {
    if (this.mouseListener)
      window.removeEventListener("mousemove", this.mouseListener);
  }

  public restartTest(): void {
    this.data = JSON.parse(JSON.stringify(testDataOnStart)); // Глубокое копирование
    this.timeStart = new Date().getTime();
  }

  public getTestData(): TestDataBuffer {
    return this.data;
  }

  public async downloadTestDataJson(): Promise<void> {
    // Минифицируем JSON и заменяем ключи на короткие
    const minifiedData = this.minifyJsonKeys(this.data);
    const jsonStr = JSON.stringify(minifiedData); // без пробелов

    // Конвертируем строку в бинарный буфер (Uint8Array)
    const encoder = new TextEncoder();
    const binaryData = encoder.encode(jsonStr);

    // --- Создаем Blob для скачивания ---
    const blob = new Blob([binaryData], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // Скачивание локально
    const a = document.createElement("a");
    a.href = url;
    a.download = "minified-data.json";
    document.body.appendChild(a);
    // a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);

    // --- Отправка на сервер ---
    try {
      const formData = new FormData();
      formData.append(
        "file",
        new File([blob], "sample.json", { type: "application/json" }),
      );

      const token = localStorage.getItem("token"); // если используешь Bearer токен
      const res = await fetch(`${serverUrl}uploadsample`, {
        method: "POST",
        body: formData,
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      if (!res.ok) {
        console.error("Ошибка при отправке файла на сервер", res.statusText);
      } else {
        const data = await res.json();
        console.log("Файл успешно отправлен на сервер", data);
      }
    } catch (err) {
      console.error("Ошибка отправки файла:", err);
    }
  }

  public writeMouseCoords(x: number, y: number, timestamp: number): void {
    this.data.writing.behaviour.push({ point: { x, y }, timestamp });
  }

  /* public writeAudioData(audioChunk: Uint8Array): void {
    if (!audioChunk || audioChunk.length === 0) return;

    this.data.voice.audio = `data:audio/webm;codecs=opus;base64,${Buffer.from(audioChunk).toString("base64")}`;
  } */

  
  public writeAudioDataReady(autio64: string): void {
    this.data.voice.audio = autio64;
  }

  public writeImageDataReady(imageData64: string): void {
    this.data.writing.image = imageData64;
  }


  public writeKeyboardData(key: string, timestamp?: number): void {
    const time = (timestamp || new Date().getTime()) - this.timeStart;
    if (!this.data.printing.behaviour) {
      this.data.printing.behaviour = [];
    }
    this.data.printing.behaviour.push({ key, timestamp: time });
  }
}

export const dataWriter = DataBufferWriter.getInstance();
